import React, { useEffect, useState } from "react";
import {
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Collapse,
  Link,
  Stack,
} from "@mui/material";
import {
  Launch as LaunchIcon,
  Upload as UploadIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import AIIcon from "@mui/icons-material/Psychology";
import BaseDialog from "../../../utils/dialog/BaseDialog";

const ModernAIButton = ({ setDialogOpen }) => {
  return (
    <Button
      variant='outlined'
      startIcon={<AIIcon />}
      onClick={() => setDialogOpen(true)}
    >
      AI Assistant
    </Button>
  );
};

const filterDefinedUrls = (items) =>
  items.filter((item) => item?.url && String(item.url).trim());

const geminiLabel = "Gemini Gem";
const chatgptLabel = "Open ChatGPT";

const getUrlOptions = (type) => {
  switch (type) {
    case "fa_addition":
      return filterDefinedUrls([
        {
          url: process.env.REACT_APP_FA_ADDITION_AI_URL,
          label: chatgptLabel,
        },
        {
          url: process.env.REACT_APP_FA_ADDITION_GEMINI_URL,
          label: geminiLabel,
        },
      ]);
    case "details_pl":
      return filterDefinedUrls([
        {
          url: process.env.REACT_APP_DETAILS_PL_AI_URL,
          label: chatgptLabel,
        },
        {
          url: process.env.REACT_APP_DETAILS_PL_GEMINI_URL,
          label: geminiLabel,
        },
      ]);
    case "details_invest_pl":
      return filterDefinedUrls([
        {
          url: process.env.REACT_APP_DETAILS_INVEST_PL_AI_URL,
          label: chatgptLabel,
        },
      ]);
    case "fa_register":
      return filterDefinedUrls([
        {
          url: process.env.REACT_APP_FA_REGISTER_AI_URL,
          label: chatgptLabel,
        },
        {
          url: process.env.REACT_APP_FA_REGISTER_GEMINI_URL,
          label: geminiLabel,
        },
      ]);
    default:
      return [];
  }
};

const getQuickHighlights = (type) => {
  switch (type) {
    case "fa_addition":
      return [
        "Paste or upload fixed-asset data in the assistant, then bring the table back into Tax Savy.",
        'Rename column "Likely tax treatment" to "CA Name" before upload.',
      ];
    case "details_pl":
    case "details_invest_pl":
      return [
        "Upload your management P&L to the assistant, then paste the output into the Detailed P&L template.",
      ];
    default:
      return ["Choose a model below to open the assistant in a new tab."];
  }
};

const getSteps = (type) => {
  switch (type) {
    case "fa_addition":
      return [
        {
          id: 1,
          title: "Load fixed asset data into the chat",
          description: "Get your data into ChatGPT using one of these methods:",
          options: [
            "(a) Copy and paste the data from a spreadsheet into ChatGPT",
            "(b) Upload the file directly into ChatGPT",
          ],
          icon: <UploadIcon color='primary' />,
        },
        {
          id: 2,
          title: "Copy data back to spreadsheet",
          description: "Extract the AI-processed data using either method:",
          options: [
            '(a) Use the "copy table" button at the top right of each ChatGPT response',
            "(b) Prompt ChatGPT to export the table into an Excel file",
          ],
          icon: <CopyIcon color='primary' />,
        },
        {
          id: 3,
          title: 'Review "Likely tax treatment" output from ChatGPT',
          description: "Carefully review the AI recommendations for accuracy",
          icon: <CloudUploadIcon color='primary' />,
        },
        {
          id: 4,
          title: 'Rename column title "Likely tax treatment" with "CA Name"',
          description:
            "Update the column header to match Tax Savy requirements",
          icon: <EditIcon color='primary' />,
        },
        {
          id: 5,
          title: "Upload into Tax Savy",
          description: "Import your processed data back into the system",
          icon: <CloudUploadIcon color='primary' />,
        },
      ];
    case "details_pl":
    case "details_invest_pl":
      return [
        {
          id: 1,
          title: "Load management P&L",
          description:
            "Upload an image, PDF, or Excel file into the custom prompt",
          icon: <UploadIcon color='primary' />,
        },
        {
          id: 2,
          title: "Download Detailed P&L template",
          description: "Get the standard template from Detailed P&L",
          icon: <DownloadIcon color='primary' />,
        },
        {
          id: 3,
          title: "Copy and paste output",
          description: "Insert ChatGPT’s output into the Detailed P&L template",
          icon: <CopyIcon color='primary' />,
        },
        {
          id: 4,
          title: "Review and upload",
          description:
            "Check the classification carefully and upload the completed template",
          icon: <CloudUploadIcon color='primary' />,
        },
      ];
    default:
      return null;
  }
};

const AIAssistantDialog = ({ type, open, onClose }) => {
  const [guideOpen, setGuideOpen] = useState(false);
  const urlOptions = getUrlOptions(type);
  const steps = getSteps(type) || [];
  const highlights = getQuickHighlights(type);

  useEffect(() => {
    if (open) setGuideOpen(false);
  }, [open]);

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title='AI Assistant'
      subtitle='Accelerate your workflow with intelligent data analysis.'
      icon={<AIIcon sx={{ fontSize: 28 }} />}
      maxWidth='sm'
      paperSx={{ maxHeight: "85vh" }}
      actions={
        <>
          <Button onClick={onClose} variant='outlined' size='medium'>
            Close
          </Button>
        </>
      }
    >
      {/* Primary: launch / model choice */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        {urlOptions.length > 0 ? (
          urlOptions.map((opt, index) => (
            <Button
              key={`${type}-ai-url-${index}`}
              onClick={() => window.open(opt.url, "_blank")}
              variant='contained'
              size='large'
              fullWidth
              endIcon={<LaunchIcon />}
              sx={{
                py: 1.25,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1rem",
              }}
            >
              {opt.label}
            </Button>
          ))
        ) : (
          <Typography variant='body2' color='warning.main'>
            AI link is not configured for this screen.
          </Typography>
        )}
      </Stack>

      {/* Collapsed essentials */}
      <Box
        sx={{
          mb: 2,
          p: 2,
          borderRadius: 1,
          bgcolor: (t) =>
            t.palette.mode === "dark"
              ? "rgba(144, 202, 249, 0.08)"
              : alpha(t.palette.primary.main, 0.06),
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
          Quick reminders
        </Typography>
        <Box component='ul' sx={{ m: 0, pl: 2.5, mb: 0 }}>
          {highlights.map((line, i) => (
            <Typography
              key={i}
              component='li'
              variant='body2'
              color='text.secondary'
              sx={{ mb: i < highlights.length - 1 ? 0.75 : 0 }}
            >
              {line}
            </Typography>
          ))}
        </Box>
      </Box>

      <Link
        component='button'
        type='button'
        variant='body2'
        onClick={() => setGuideOpen((v) => !v)}
        sx={{
          cursor: "pointer",
          mb: guideOpen ? 1 : 0,
          display: "inline-block",
          textAlign: "left",
        }}
      >
        {guideOpen ? "Close the guide" : "Need help? View the guide here."}
      </Link>

      <Collapse in={guideOpen} timeout='auto' unmountOnExit>
        {type === "fa_addition" && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ display: "block", mb: 1.5 }}
          >
            Tip: Be specific about asset descriptions for better
            recommendations.
          </Typography>
        )}
        <List sx={{ width: "100%", py: 0 }} disablePadding>
          {steps.map((step, index) => (
            <React.Fragment key={step.id}>
              <ListItem alignItems='flex-start' sx={{ py: 1.5, px: 0 }}>
                <ListItemIcon sx={{ mt: 0.5, minWidth: 40 }}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      backgroundColor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                    }}
                  >
                    {step.id}
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant='subtitle2' fontWeight={600}>
                      {step.title}
                    </Typography>
                  }
                  secondary={
                    <Box component='span' display='block'>
                      <Typography
                        variant='body2'
                        color='text.secondary'
                        component='span'
                        display='block'
                        sx={{ mt: 0.5 }}
                      >
                        {step.description}
                      </Typography>
                      {step.options && (
                        <Box sx={{ ml: 1.5, mt: 0.5 }} component='span'>
                          {step.options.map((option, idx) => (
                            <Typography
                              key={idx}
                              variant='body2'
                              color='text.secondary'
                              display='block'
                              sx={{ mb: 0.25 }}
                            >
                              {option}
                            </Typography>
                          ))}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItem>
              {index < steps.length - 1 && <Divider component='li' />}
            </React.Fragment>
          ))}
        </List>
      </Collapse>

      <Box
        sx={{
          mt: guideOpen ? 3 : 2,
          display: "flex",
          gap: 1,
        }}
      >
        <InfoIcon sx={{ fontSize: 14, color: "text.disabled", mt: 0.25 }} />

        <Typography
          variant='caption'
          color='text.disabled'
          sx={{
            lineHeight: 1.5,
            fontSize: "0.75rem",
          }}
        >
          AI output is for reference only. Please verify critical details with a
          professional.
        </Typography>
      </Box>
    </BaseDialog>
  );
};

// Button component to trigger the dialog
const AIAssistantButton = ({ type }) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  return (
    <>
      <ModernAIButton setDialogOpen={setDialogOpen} />

      <AIAssistantDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        type={type}
      />
    </>
  );
};

export { AIAssistantDialog, AIAssistantButton };
